import { useState } from "react";
import {
    Container,
    Box,
    Paper,
    Button,
    TextField,
    Grid,
    Typography,
    Divider,
} from "@mui/material";

import ChronoSection from "../components/ChronoSection";
import ChronoTitle from "../components/ChronoTitle";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

// ⚠️ Copié tel quel depuis Timer.jsx (même format TN Tag Heuer officiel)
// — c'est CE format que le backend (timingRuns.js) sait déjà parser
// correctement (dossard, canal, heure). Une impulsion simulée avec ce
// même générateur est donc indiscernable d'une vraie, ce qui permet de
// tester TOUT le pipeline (ingestion → classement → multi-manche → FIS),
// pas seulement l'affichage brut.
function buildTagHeuerFrame(seq, channel, candidate, dateUTC) {
    const epoch2000 = Date.UTC(2000, 0, 1);
    const dayUTC = Date.UTC(
        dateUTC.getUTCFullYear(),
        dateUTC.getUTCMonth(),
        dateUTC.getUTCDate()
    );
    const days = Math.round((dayUTC - epoch2000) / 86400000);
    const dayStr = String(days).padStart(5, "0");

    const hh = String(dateUTC.getUTCHours()).padStart(2, "0");
    const mm = String(dateUTC.getUTCMinutes()).padStart(2, "0");
    const ss = String(dateUTC.getUTCSeconds()).padStart(2, "0");
    const frac = String(Math.floor(dateUTC.getUTCMilliseconds() * 1000)).padStart(5, "0");

    const timeStr = `${hh}:${mm}:${ss}.${frac}`;

    let channelLabel;
    if (/^M\d+$/.test(channel)) {
        channelLabel = channel.replace("M", "").padStart(2, "0");
    } else {
        channelLabel = String(channel).padStart(2, "0");
    }

    const candidateStr = String(candidate ?? 0).padStart(4, "0");
    const seqStr = String(seq).padStart(4, "0");

    return `TN ${candidateStr} ${seqStr} ${channelLabel} ${timeStr} ${dayStr}`;
}

async function sendEvent(event) {
    try {
        const res = await fetch(SERVER_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-Api-Key": API_KEY },
            body: JSON.stringify(event),
        });
        return res.ok;
    } catch {
        return false;
    }
}

const DEFAULT_CHANNELS = [
    { label: "Canal 1 (ex: Départ)", channel: "M1" },
    { label: "Canal 2 (ex: Inter 1)", channel: "M2" },
    { label: "Canal 3 (ex: Inter 2)", channel: "M3" },
    { label: "Canal 4 (ex: Arrivée)", channel: "M4" },
];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function Simulateur4Canaux() {
    const [chronoId, setChronoId] = useState("chrono-simu");
    const [userName, setUserName] = useState("Simulateur");
    const [gmtOffset, setGmtOffset] = useState(0);
    const [batchDelayMs, setBatchDelayMs] = useState(1000);

    // Un slot par canal : {label, channel, bib, sequence, lastResult}.
    // `sequence` est indépendant PAR CANAL — comme sur un vrai poste, où
    // chaque canal a sa propre numérotation.
    const [slots, setSlots] = useState(
        DEFAULT_CHANNELS.map((c) => ({ ...c, bib: "", sequence: 1, lastResult: null, sending: false }))
    );

    const updateSlot = (index, patch) => {
        setSlots((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    };

    // Envoie UNE impulsion pour le canal `index` — avec ou sans dossard,
    // exactement comme le bouton "IMPULSION" de Timer.jsx.
    const sendOne = async (index) => {
        const slot = slots[index];
        updateSlot(index, { sending: true });

        const now = new Date();
        const adjustedUTC = new Date(now.getTime() + gmtOffset * 3600 * 1000);
        const frame = buildTagHeuerFrame(slot.sequence, slot.channel, slot.bib || null, adjustedUTC);

        const event = {
            chrono_id: chronoId,
            channel: slot.channel,
            candidate: slot.bib || null,
            sequence: slot.sequence,
            source_format: "tagheuer",
            user: userName,
            raw_value: frame,
            timestamp: adjustedUTC.toISOString(),
            local_timestamp: now.toISOString(),
        };

        const ok = await sendEvent(event);

        updateSlot(index, {
            sending: false,
            sequence: slot.sequence + 1,
            lastResult: {
                ok,
                time: adjustedUTC.toLocaleTimeString("fr-CH", { hour12: false }),
                bib: slot.bib || "(sans dossard)",
                raw: frame,
            },
        });

        return ok;
    };

    // Envoie les 4 canaux DANS L'ORDRE (1 → 2 → 3 → 4), séparés du délai
    // choisi — simule un concurrent qui passe départ/inter/inter/arrivée
    // avec un temps de course réaliste, plutôt que 4 impulsions à la
    // même milliseconde (temps = 0, peu utile pour tester le calcul).
    const [batchRunning, setBatchRunning] = useState(false);
    const sendAllInOrder = async () => {
        setBatchRunning(true);
        try {
            for (let i = 0; i < slots.length; i++) {
                await sendOne(i);
                if (i < slots.length - 1) await sleep(batchDelayMs);
            }
        } finally {
            setBatchRunning(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <ChronoSection>
                <ChronoTitle>Simulateur 4 canaux</ChronoTitle>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Envoie de vraies impulsions au format TN (identique à Timer.jsx) sur jusqu'à 4
                    canaux, pour tester le pipeline complet sans matériel de chronométrage. Le
                    dossard est optionnel sur chaque canal — laissez vide pour tester la
                    propagation automatique.
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Chrono ID (partagé par les 4 canaux)"
                            fullWidth
                            size="small"
                            value={chronoId}
                            onChange={(e) => setChronoId(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Utilisateur"
                            fullWidth
                            size="small"
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Décalage horaire (GMT)"
                            fullWidth
                            size="small"
                            select
                            SelectProps={{ native: true }}
                            value={gmtOffset}
                            onChange={(e) => setGmtOffset(Number(e.target.value))}
                        >
                            <option value={-3}>GMT-3</option>
                            <option value={-2}>GMT-2</option>
                            <option value={-1}>GMT-1</option>
                            <option value={0}>GMT</option>
                            <option value={1}>GMT+1</option>
                            <option value={2}>GMT+2</option>
                            <option value={3}>GMT+3</option>
                        </TextField>
                    </Grid>
                </Grid>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
                    <TextField
                        label="Délai entre canaux (ms)"
                        size="small"
                        type="number"
                        value={batchDelayMs}
                        onChange={(e) => setBatchDelayMs(Math.max(0, Number(e.target.value)))}
                        sx={{ width: 200 }}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={batchRunning}
                        onClick={sendAllInOrder}
                    >
                        {batchRunning ? "Envoi en cours…" : "▶ Envoyer les 4 canaux dans l'ordre"}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                        Simule un concurrent passant les 4 points avec un temps de course réaliste
                        (canal 1 puis 2 puis 3 puis 4, espacés du délai ci-dessus).
                    </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                    {slots.map((slot, i) => (
                        <Grid item xs={12} sm={6} key={i}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                                    <TextField
                                        label="Nom (repère)"
                                        size="small"
                                        value={slot.label}
                                        onChange={(e) => updateSlot(i, { label: e.target.value })}
                                        sx={{ flex: 2 }}
                                    />
                                    <TextField
                                        label="Canal"
                                        size="small"
                                        value={slot.channel}
                                        onChange={(e) => updateSlot(i, { channel: e.target.value })}
                                        sx={{ flex: 1 }}
                                    />
                                </Box>

                                <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
                                    <TextField
                                        label="Dossard (optionnel)"
                                        size="small"
                                        fullWidth
                                        value={slot.bib}
                                        onChange={(e) => updateSlot(i, { bib: e.target.value })}
                                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                    />
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        disabled={slot.sending || batchRunning}
                                        onClick={() => sendOne(i)}
                                        sx={{ minWidth: 140, height: 40 }}
                                    >
                                        {slot.sending ? "…" : "IMPULSION"}
                                    </Button>
                                </Box>

                                <Typography variant="caption" color="text.secondary" display="block">
                                    Prochaine séquence : {slot.sequence}
                                </Typography>

                                {slot.lastResult && (
                                    <Box
                                        sx={{
                                            mt: 1,
                                            p: 1,
                                            borderRadius: 1,
                                            bgcolor: slot.lastResult.ok ? "success.light" : "error.light",
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        {slot.lastResult.ok ? "✓ Envoyé" : "✗ Échec envoi"} — dossard{" "}
                                        {slot.lastResult.bib} à {slot.lastResult.time}
                                        <Box sx={{ fontFamily: "monospace", mt: 0.5, wordBreak: "break-all" }}>
                                            {slot.lastResult.raw}
                                        </Box>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </ChronoSection>
        </Container>
    );
}